import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * BrowserFrame — CSS-only browser chrome around a product screenshot.
 *
 * The screenshots committed under `marketing/assets/screens/` are plain
 * 1440×900 captures with no chrome baked in — this component supplies the
 * rounded top bar, traffic-light dots, and an address pill so a raw <img>
 * reads as "a real screen inside a browser" rather than a floating PNG.
 *
 * Dark-aware; no JS beyond the wrapper, so it's cheap to drop around any
 * screenshot without extra state.
 */
export function BrowserFrame({
  children,
  url = 'worqly.io/dashboard',
  className,
}: {
  children: React.ReactNode;
  /** Text shown in the fake address pill. Defaults to the app URL shape. */
  url?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[22px] bg-white dark:bg-ink-900',
        'ring-1 ring-ink/10 dark:ring-white/10 shadow-card-lg',
        className,
      )}
    >
      {/* Top chrome bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-pearl-50 dark:bg-white/[0.05] border-b border-ink/8 dark:border-white/10">
        <div className="flex items-center gap-1.5 shrink-0" aria-hidden="true">
          <span className="size-2.5 rounded-full bg-rose-400" />
          <span className="size-2.5 rounded-full bg-amber-400" />
          <span className="size-2.5 rounded-full bg-lime-neon" />
        </div>
        <div className="flex-1 flex justify-center min-w-0">
          <span
            aria-hidden="true"
            className="inline-flex items-center h-6 px-3 max-w-[260px] truncate rounded-full bg-white dark:bg-white/10 ring-1 ring-ink/8 dark:ring-white/10 text-[11px] font-mono text-ink/50 dark:text-white/55"
          >
            {url}
          </span>
        </div>
        <div className="w-[46px] shrink-0" aria-hidden="true" />
      </div>

      {/* Screenshot surface */}
      <div className="bg-pearl-50 dark:bg-white/[0.02]">{children}</div>
    </div>
  );
}
